import React from "react"
import { Formik, Form as FormikForm, Field } from "formik"
import { Row } from "react-bootstrap"
import * as Yup from "yup"
import FormTextbox from "../../shared/formTextbox"
import FormFieldDisableAutoComplete from "../../shared/formFieldDisableAutoComplete"
import FormRadioButton from "../../shared/formRadioButton"
import FormDatePickerTextBox from "../../shared/formDatePickerTextBox"
import FormTimeTextBox from "../../shared/formTimeTextBox"
import FormSectionTitle from "../../shared/formSectionTitle"
import FormAmountTextBox from "../../shared/formAmountTextBox"

import FormDropdownList from "../../shared/formDropdownList"
import FormDropdownMulti from "../../shared/formDropdownMulti"
import FormEditUpload from "../../shared/formEditUpload"

import {
  controlEditBenefit,
  updateValidBenfitFormData,
  updateClickedNext,
  updateFormIsValid,
  pageIncrement,
  completeBenefitForm,
} from "../../../redux/actions"

import * as Constants from "../../../common/constants"
import * as Regex from "../../../common/regex"
import * as ErrorMessage from "../../../common/errorMessage"
import * as CommonFunction from "../../../common/function"
import * as Alert from "../../../services/sweetalertService"

import { useDispatch, useSelector, shallowEqual, batch } from "react-redux"

const BicycleClaimsForm = React.memo(({ coverageDetail }) => {
  const dispatch = useDispatch()
  const formRef = React.useRef()

  const {
    editBenefits,
    allProofDocuments,
    claimUploadListModel,
    clickedNext,
    isComeWithChildCoverage,
    tempBenefitInfo,
    pageNumber,
    chosenedBenefits,
  } = useSelector(state => state.uiData)

  const claimData = useSelector(state => state.claimData, shallowEqual)

  const {
    Category: coverageCategory,
    CoverageCode: coverageCode,
    CoverageDetailId: coverageDetailId,
    CoverageDetailName: coverageDetailName,
  } = coverageDetail

  const formName = coverageCode

  //dropdown list
  const { BicycleNature, BicycleDamageCause, BicycleDamagePart } = useSelector(
    state => state.dropdownData
  )

  let multiBicycleDamagePart = []
  for (var part of BicycleDamagePart) {
    multiBicycleDamagePart.push({
      label: part.Description,
      value: part.Description,
    })
  }

  useSelector(state => {
    if (!state.claimData[coverageCategory][coverageCode]) {
      state.claimData[coverageCategory][coverageCode] = {}
    }
  }, shallowEqual)

  const validationSchema = Yup.object().shape({
    occurrenceNature: Yup.string().required(ErrorMessage.BicycleClaimNature),

    purchaseValue: Yup.number()
      .transform(value => (isNaN(value) ? undefined : value))
      .min(1, ErrorMessage.BicyclePurchaseValue),

    make: Yup.string().required(ErrorMessage.BicycleMake),
    model: Yup.string().required(ErrorMessage.BicycleModel),
    purchaseDate: Yup.string().required(ErrorMessage.BicyclePurchaseDate),
    serialNumber: Yup.string().required(ErrorMessage.BicycleSerialNumber),
    occurrenceDate: Yup.date()
      .when("occurrenceNature", {
        is: val => val === "Accidental Damage" || val === "Malicious Damage",
        then: Yup.date()
          .required(ErrorMessage.BicycleDamageDate)
          .test(
            "occurrenceDate",
            ErrorMessage.BicycleDamageEarlierThanPurchase,
            value => {
              return CommonFunction.isAfterDateTime(
                formRef.current.values.purchaseDate,
                "00:00",
                value,
                "00:00"
              )
            }
          ),
      })
      .when("occurrenceNature", {
        is: val => val === "Loss in Transit" || val === "Theft",
        then: Yup.date()
          .required(ErrorMessage.BicycleLossDate)
          .test(
            "occurrenceDate",
            ErrorMessage.BicycleDamageEarlierThanPurchase,
            value => {
              return CommonFunction.isAfterDateTime(
                formRef.current.values.purchaseDate,
                "00:00",
                value,
                "00:00"
              )
            }
          ),
      }),

    occurrenceTime: Yup.string()
      .when("occurrenceNature", {
        is: val => val === "Accidental Damage" || val === "Malicious Damage",
        then: Yup.string()
          .required(ErrorMessage.BicycleDamageTime)
          .matches(Regex.timepattern, ErrorMessage.BicycleDamageTime)
          .test("occurrenceTime", ErrorMessage.futureTimeErrorMsg, value => {
            return CommonFunction.notFutureDatime(
              formRef.current.values.occurrenceDate,
              value
            )
          })
          .test(
            "occurrenceTime",
            ErrorMessage.BicycleDamageEarlierThanPurchase,
            value => {
              return CommonFunction.isAfterDateTime(
                formRef.current.values.purchaseDate,
                "00:00",
                formRef.current.values.occurrenceDate,
                value
              )
            }
          ),
      })
      .when("occurrenceNature", {
        is: val => val === "Loss in Transit" || val === "Theft",
        then: Yup.string()
          .required(ErrorMessage.BicycleLossTime)
          .matches(Regex.timepattern, ErrorMessage.BicycleLossTime)
          .test("occurrenceTime", ErrorMessage.futureTimeErrorMsg, value => {
            return CommonFunction.notFutureDatime(
              formRef.current.values.occurrenceDate,
              value
            )
          })
          .test(
            "occurrenceTime",
            ErrorMessage.BicycleLossEarlierThanPurchase,
            value => {
              return CommonFunction.isAfterDateTime(
                formRef.current.values.purchaseDate,
                "00:00",
                formRef.current.values.occurrenceDate,
                value
              )
            }
          ),
      }),

    occurrenceReason: Yup.string().when("occurrenceNature", {
      is: "Accidental Damage",
      then: Yup.string().required(ErrorMessage.BicycleDamageCause),
    }),

    damagePart: Yup.string().when("occurrenceNature", {
      is: val => val === "Accidental Damage" || val === "Malicious Damage",
      then: Yup.string().required(ErrorMessage.BicycleDamagePart),
    }),

    occurrenceLocation: Yup.string().when("occurrenceNature", {
      is: val => val === "Loss in Transit" || val === "Theft",
      then: Yup.string().required(ErrorMessage.BicycleLossLocation),
    }),

    claimAmount: Yup.number()
      .transform(value => (isNaN(value) ? undefined : value))
      .min(1, ErrorMessage.claimAmount),
  })

  React.useEffect(() => {
    let nature
    if (BicycleNature && coverageDetailName) {
      nature = BicycleNature.filter(x => {
        return coverageDetailName.includes(x.Description)
      })

      if (nature.length > 0)
        formRef.current.setFieldValue("occurrenceNature", nature[0].Description)
    }
  }, [BicycleNature])

  return (
    <Formik
      initialValues={Object.assign(
        {
          occurrenceNature: "",
          purchaseValue: 0,
          make: "",
          model: "",
          purchaseDate: "",
          serialNumber: "",
          occurrenceDate: "",
          occurrenceTime: "",
          occurrenceReason: "",
          occurrenceLocation: "",
          damagePart: "",
          reportedToPolice: "",
          claimAmount: 0,
        },
        claimData[coverageCategory][coverageCode]
      )}
      enableReinitialize={true}
      validationSchema={validationSchema}
      validateOnMount={true}
      innerRef={formRef}
      onSubmit={(values, actions) => {}}
    >
      {({ handleSubmit, values }) => (
        <>
          <FormikForm onSubmit={handleSubmit}>
            {
              <Row
                className={
                  "form-group " + (editBenefits[coverageCode] ? "" : "hide")
                }
              >
                <div className="col-md-9 col-7 textforinput marginTop10px benefitEditTitle">
                  <p>{coverageDetailName}</p>
                </div>
                <div className="col-md-3 col-5 marginTop10px">
                  <div
                    className="cursorPointer"
                    role="button"
                    tabIndex="0"
                    onClick={() => dispatch(controlEditBenefit(coverageCode))}
                  >
                    <p className="doneEdit">DONE EDIT</p>
                  </div>
                </div>
              </Row>
            }
            {/* Fake fields are a workaround for chrome autofill getting the wrong fields */}
            <Field
              textDesc={"Disable Auto Complete"}
              name="disableAutoComplete"
              component={FormFieldDisableAutoComplete}
            />

            {/* hide this field */}
            {"" && (
              <Field
                disabled={true}
                textDesc={"Nature of Claim"}
                name="occurrenceNature"
                component={FormDropdownList}
                dropdownItem={BicycleNature}
              />
            )}

            <Field
              textDesc="Bicycle Purchase Value"
              name="purchaseValue"
              component={FormAmountTextBox}
              maxLength={14}
            />

            <Field
              textDesc={"Bicycle Make"}
              maxLength={300}
              name="make"
              component={FormTextbox}
            />

            <Field
              textDesc={"Bicycle Model"}
              maxLength={300}
              name="model"
              component={FormTextbox}
            />

            <Field
              textDesc="Date of Purchase"
              name="purchaseDate"
              component={FormDatePickerTextBox}
              disableBefore={"disable"}
            />

            <Field
              textDesc={"Bicycle Serial Number"}
              maxLength={100}
              name="serialNumber"
              component={FormTextbox}
            />

            {values.occurrenceNature &&
              (values.occurrenceNature === "Accidental Damage" ||
                values.occurrenceNature === "Malicious Damage") && (
                <>
                  {/* Fake fields are a workaround for chrome autofill getting the wrong fields */}
                  <Field
                    textDesc={"Disable Auto Complete"}
                    name="disableAutoComplete2"
                    component={FormFieldDisableAutoComplete}
                  />
                  <Field
                    textDesc="Date damage was discovered"
                    name="occurrenceDate"
                    component={FormDatePickerTextBox}
                  />

                  <Field
                    textDesc="Time damage was discovered (HH:mm)"
                    name="occurrenceTime"
                    component={FormTimeTextBox}
                  />

                  {values.occurrenceNature === "Accidental Damage" && (
                    <Field
                      textDesc={"Cause of Damage"}
                      name="occurrenceReason"
                      component={FormDropdownList}
                      dropdownItem={BicycleDamageCause}
                    />
                  )}

                  <Field
                    textDesc="Damaged bicycle part"
                    coverageCategory={coverageCategory}
                    coverageCode={coverageCode}
                    name="damagePart"
                    component={FormDropdownMulti}
                    dropdownItem={multiBicycleDamagePart}
                  />
                </>
              )}

            {values.occurrenceNature &&
              (values.occurrenceNature === "Loss in Transit" ||
                values.occurrenceNature === "Theft") && (
                <>
                  {/* Fake fields are a workaround for chrome autofill getting the wrong fields */}
                  <Field
                    textDesc={"Disable Auto Complete"}
                    name="disableAutoComplete3"
                    component={FormFieldDisableAutoComplete}
                  />
                  <Field
                    textDesc="Date of Loss"
                    name="occurrenceDate"
                    component={FormDatePickerTextBox}
                  />

                  <Field
                    textDesc="Time of Loss (HH:mm)"
                    name="occurrenceTime"
                    component={FormTimeTextBox}
                  />

                  <Field
                    textDesc={"Location of Loss"}
                    maxLength={100}
                    name="occurrenceLocation"
                    component={FormTextbox}
                  />
                </>
              )}

            {values.occurrenceNature &&
              (values.occurrenceNature === "Malicious Damage" ||
                values.occurrenceNature === "Loss in Transit" ||
                values.occurrenceNature === "Theft") && (
                <>
                  <Field
                    textDesc={"Have you reported this to the police?"}
                    name="reportedToPolice"
                    component={FormRadioButton}
                  />
                </>
              )}

            <Field
              textDesc={"Amount Claimed(SGD)"}
              maxLength={14}
              name="claimAmount"
              component={FormAmountTextBox}
            />

            <FormEditUpload
              editBenefits={editBenefits[coverageCode]}
              coverageDetailId={coverageDetailId}
              coverageDetailName={coverageDetailName}
              allProofDocuments={allProofDocuments}
              claimUploadListModel={claimUploadListModel}
            />

            <HandleFormValidation
              thisPageClickedNext={clickedNext[Constants.benefitFormPageNumber]}
              reviewPageClickedNext={clickedNext[Constants.reviewPageNumber]}
              formRef={formRef}
              values={values}
              thisPageNumber={Constants.benefitFormPageNumber}
              dispatch={dispatch}
              formName={formName}
              coverageCategory={coverageCategory}
              coverageCode={coverageCode}
              tempBenefitInfo={tempBenefitInfo}
              chosenedBenefits={chosenedBenefits}
            />
            <HandleClaimAmountUpdate
              claimAmount={values.claimAmount}
              dispatch={dispatch}
              pageNumber={pageNumber}
              coverageCategory={coverageCategory}
              coverageCode={coverageCode}
              formRef={formRef}
            />
          </FormikForm>
        </>
      )}
    </Formik>
  )
})

const HandleFormValidation = ({
  thisPageClickedNext,
  reviewPageClickedNext,
  formRef,
  values,
  thisPageNumber,
  dispatch,
  formName,
  coverageCategory,
  coverageCode,
  tempBenefitInfo,
  chosenedBenefits,
}) => {
  React.useEffect(() => {
    if (
      (thisPageClickedNext && coverageCode === tempBenefitInfo.CoverageCode) ||
      (reviewPageClickedNext &&
        CommonFunction.checkBenefitChosened(chosenedBenefits, coverageCode))
    ) {
      // console.log(formRef.current)
      formRef.current.validateForm().then(errors => {
        // console.log(errors)

        batch(() => {
          if (Object.entries(errors).length === 0) {
            // console.log("PASS")

            dispatch(
              updateValidBenfitFormData(coverageCategory, coverageCode, values)
            )
            if (thisPageClickedNext) {
              dispatch(pageIncrement())
              dispatch(completeBenefitForm(coverageCode))
            } else {
              dispatch(updateFormIsValid(formName, true))
            }
          } else {
            if (thisPageClickedNext) {
              Alert.missingInfoAlert()
            } else {
              dispatch(updateFormIsValid(formName, false))
            }
            formRef.current.submitForm() // Set touched to all fields
          }

          dispatch(updateClickedNext(thisPageNumber, false))
        })
      })
    }
  }, [
    thisPageClickedNext,
    reviewPageClickedNext,
    formRef,
    values,
    thisPageNumber,
    dispatch,
    formName,
    coverageCategory,
    coverageCode,
    tempBenefitInfo,
  ])
  return null
}

const HandleClaimAmountUpdate = ({
  claimAmount,
  dispatch,
  pageNumber,
  coverageCategory,
  coverageCode,
  formRef,
}) => {
  React.useEffect(() => {
    if (pageNumber === Constants.reviewPageNumber) {
      setTimeout(() => {
        batch(() => {
          dispatch(
            updateValidBenfitFormData(
              coverageCategory,
              coverageCode,
              formRef.current.values
            )
          )
        })
      }, 1000)
    }
  }, [claimAmount])

  return null
}

export default BicycleClaimsForm
